import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { useDialog } from '@/common/dialog/DialogContext';
import { MainBox } from '@/common/layout/MainBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, Checkbox, ListItemText, MenuItem, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { McoInvPrioritizationBubbleChart } from './McoInvPrioritizationBubbleChart';
import { McoInvProgressChart } from './McoInvProgressChart';
import { McoInvStatusDefinitionsDialog } from './McoInvStatusDefinitionsDialog';
import { McoValChart } from './McoValChart';

export const McoInvDashboard = () => {

  const rsrc = useRsrc();

  const { openDialog } = useDialog();
  const { navState, assign } = useNavState();
  const dashboardNavState = navState['mcoInvDashboardState'] ?? {};

  const [periods, setPeriods] = useState();
  const [selectedPeriod, setSelectedPeriod] = useState();

  const [pbms, setPbms] = useState();
  const [selectedPbms, setSelectedPbms] = useState();

  const [ctrts, setCtrts] = useState();
  const [selectedCtrts, setSelectedCtrts] = useState([]);

  const [dueDates, setDueDates] = useState();
    const [selectedDueDates, setSelectedDueDates] = useState();

  useEffect(() => {
    rsrc.data('sc', 'MCO_DASHBOARD_FILTERS_DATA_PLUGIN', {}).then(res => {
      setPeriods(res.periods);
      // Restore nav state
      if (dashboardNavState.selectedPeriodId) {
        const period = res.periods.find(p => p.id === dashboardNavState.selectedPeriodId);
        if (period) setSelectedPeriod(period);
      } 
      // or set to first period
      else if (res.periods?.length > 0) {
        const selectedPeriod = res.periods[0];
        setSelectedPeriod(selectedPeriod);
      }

      setPbms(res.pbms);
       if (dashboardNavState.selectePbmCodes) {
        const matchedPbms = res.pbms.filter(pbm => 
          dashboardNavState.selectePbmCodes.includes(pbm.code)
        );
        if (matchedPbms.lenght > 0) setSelectedPbms(matchedPbms);
      } else {
        setSelectedPbms(res.pbms);
      }

      setCtrts(res.ctrts);
      // Restore nav state
      if (dashboardNavState.selectedCtrtCodes) {
        const matchedCtrts = res.ctrts.filter(ctrt => 
          dashboardNavState.selectedCtrtCodes.includes(ctrt.code)
        );
        if (matchedCtrts.length > 0) setSelectedCtrts(matchedCtrts);
      } else {
        setSelectedCtrts(res.ctrts);
      }

      setDueDates(res.dueDates);
      // Restore nav state
      if (dashboardNavState.selectedDueDates) {
        const matchedDueDates = res.dueDates.filter(dueDate => 
          dashboardNavState.selectedDueDates.includes(dueDate.value)
        );
        if (matchedDueDates.length > 0) setSelectedDueDates(matchedDueDates);
      } else {
        setSelectedDueDates(res.dueDates);
      }

    });
  }, []);

  const openDefinitionsDialog = () => {
    openDialog(McoInvStatusDefinitionsDialog)
  }

  // ----- NavState -----
  const saveNavState = () => {
    const newDashboardNavState = {
      selectedPeriodId: selectedPeriod?.id,
      selectedPbmCodes: selectedPbms?.map(pbm => pbm.code),
      selectedCtrtCodes: selectedCtrts?.map(c => c.code),
      selectedDueDates: selectedDueDates?.map(d => d.value)
    };

    assign('mcoInvDashboardState', newDashboardNavState);
  };

  useEffect(() => {
    saveNavState();
  }, [selectedPeriod, selectedPbms, selectedCtrts]);

  if (!periods || !pbms || !ctrts || !dueDates) return null;

  return (
    <MainBox>
      {/* Title */}
      <Box sx={{
        mb: 1, display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <Title icon={fas.faTasks} title="Managed Care Invoice Dashboard" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
          <TitleButton key="comparePolFormulaButton" icon={fas.faInfoCircle} toolTip="View Definitions" onClick={openDefinitionsDialog} />
          <ExportButton />
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        {/* Period */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Invoice Period"
          value={selectedPeriod?.id || ''}
          onChange={event => {
            const period = periods.find(p => p.id === event.target.value);
            setSelectedPeriod(period);
          }}>
          {periods?.map(period => (
            <MenuItem key={period.id} value={period.id}>
              {period.code}
            </MenuItem>
          ))}
        </TextField>

        {/* PBMs */}
        <TextField fullWidth select
          variant="outlined"
          label="PBMs"
          value={selectedPbms}
          onChange={event => setSelectedPbms(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((p) => p.code).join(', ')
          }}>
          {pbms?.map(pbm => (
            <MenuItem key={pbm.code} value={pbm}>
              <Checkbox checked={selectedPbms?.some((p) => p.code === pbm.code)} />
              <ListItemText primary={pbm.code} />
            </MenuItem>
          ))}
        </TextField>

        {/* Contracts */}
        <TextField fullWidth select
          variant="outlined"
          label="Contracts"
          value={selectedCtrts}
          onChange={event => setSelectedCtrts(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((p) => p.code).join(', ')
          }}>
          {ctrts?.map(ctrt => (
            <MenuItem key={ctrt.code} value={ctrt}>
              <Checkbox checked={selectedCtrts?.some((p) => p.code === ctrt.code)} />
              <ListItemText primary={ctrt.code} />
            </MenuItem>
          ))}
        </TextField>

       {/* Due Dates */}
        <TextField fullWidth select
          variant="outlined"
          label="Due dates"
          value={selectedDueDates}
          onChange={event => setSelectedDueDates(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((p) => p.displayValue).join(', ')
          }}>
          {dueDates?.map(dueDate => (
            <MenuItem key={dueDate.value} value={dueDate}>
              <Checkbox checked={selectedDueDates?.some((p) => p.value === dueDate.value)} />
              <ListItemText primary={dueDate.displayValue} />
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <SpacerLayout>
        <Spacer vertical>

          {/* Row 1 */}
          <Spacer weights={[1, 1]}>
            <DashboardCard title="Invoice Progress" sx={{ flex: 1 }}>
              <McoInvProgressChart
                selectedPbms={selectedPbms}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDates={selectedDueDates}
              />
            </DashboardCard>

            {/* <DashboardCard title="Settlement Progress" sx={{ flex: 1 }}>
              <McoStlmntProgressChart
                selectedPbms={selectedPbms}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDates={selectedDueDates}
              />
            </DashboardCard> */}
            <DashboardCard title="Validation Chart" sx={{ flex: 1 }}>
              <McoValChart
                selectedPbms={selectedPbms}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDates={selectedDueDates}
              />
            </DashboardCard>
          </Spacer>

          {/* Row 2 */}
          <Spacer weights={[1]}>
            <DashboardCard title="Prioritization Chart" sx={{ height: '100%' }}>
              <McoInvPrioritizationBubbleChart
                selectedPbms={selectedPbms}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDates={selectedDueDates}
              />
            </DashboardCard>
            {/* <DashboardCard title="Prior Month % Completion" sx={{ height: '100%' }}>
              <McoInvPriorMonPercentCompleteChart
                selectedPbms={selectedPbms}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDates={selectedDueDates}
              />
            </DashboardCard> */}
          </Spacer>

        </Spacer>
      </SpacerLayout>

    </MainBox >
  );
};